import { useNavigate } from 'react-router-dom';
import '../styles/LegalPage.css';

function ServiceAgreement() {
    const navigate = useNavigate();

    return (
        <div className="legal-page">
            <div className="legal-container">
                <button className="legal-back" onClick={() => navigate(-1)}>
                    &larr; Back
                </button>

                <h1>Terms of Service</h1>
                <p className="legal-updated">Last Updated: April 27, 2026</p>

                <section>
                    <h2>1. Acceptance of Terms</h2>
                    <p>
                        By accessing or using the PrimeMaxing platform (the "Service"), you agree to be bound
                        by these Terms of Service ("Terms"). If you do not agree to these Terms, do not use
                        the Service.
                    </p>
                </section>

                <section>
                    <h2>2. Description of Service</h2>
                    <p>
                        PrimeMaxing provides a health information platform that allows users to upload
                        laboratory test results and view biomarker insights. The Service presents
                        health scores, wellness information, and personalized supplement and intervention
                        suggestions using proprietary algorithms and artificial intelligence.
                    </p>
                    <p>
                        The Service may also offer the ability to connect with licensed healthcare providers
                        and share your data with doctors or peers you authorize.
                    </p>
                    <p>
                        With your permission, the Service may integrate with third-party health and fitness
                        applications (such as fitness trackers, cycle trackers, and sleep monitors) to
                        incorporate additional biometric and wellness data into your health profile. You
                        control which applications are connected and may revoke access at any time.
                    </p>
                </section>

                <section>
                    <h2>3. Not Medical Advice</h2>
                    <p>
                        <strong>THE SERVICE IS NOT A SUBSTITUTE FOR PROFESSIONAL MEDICAL ADVICE, DIAGNOSIS,
                        OR TREATMENT.</strong> PrimeMaxing is not a licensed healthcare provider, medical
                        device, or clinical laboratory.
                    </p>
                    <p>
                        All scores, suggestions, insights, and AI-generated content provided through the
                        Service are for <strong>informational and educational purposes only</strong>. They are
                        generated by automated algorithms that may contain errors, omissions, or inaccuracies.
                    </p>
                    <p>
                        You should <strong>never rely solely on the Service</strong> to make health decisions.
                        Always seek the advice of a qualified physician or other healthcare professional with
                        any questions regarding a medical condition. Never disregard professional medical advice
                        or delay in seeking it because of information obtained through the Service.
                    </p>
                    <p>
                        <strong>If you think you may have a medical emergency, call your doctor or 911
                        immediately.</strong>
                    </p>
                </section>

                <section>
                    <h2>4. Healthcare Provider Access</h2>
                    <p>
                        The Service may facilitate connections between you and independent, licensed healthcare
                        providers ("Providers"). These Providers are not employees, agents, or representatives
                        of PrimeMaxing. Any medical advice, diagnosis, or treatment you receive from a Provider
                        is the sole responsibility of that Provider.
                    </p>
                    <p>
                        PrimeMaxing does not practice medicine. While the Service generates supplement
                        and intervention suggestions based on your data, these are algorithmically
                        produced informational outputs — not prescriptions or medical orders. While we
                        may verify credentials of Providers available through the platform, we do not
                        supervise, direct, or take responsibility for any clinical decisions, advice,
                        or treatment a Provider delivers. Your relationship with any Provider is
                        independent of PrimeMaxing.
                    </p>
                </section>

                <section>
                    <h2>5. User Accounts</h2>
                    <p>
                        To use the Service, you must create an account and provide accurate, complete
                        information. You are responsible for:
                    </p>
                    <ul>
                        <li>Maintaining the confidentiality of your account credentials</li>
                        <li>All activity that occurs under your account</li>
                        <li>Notifying us immediately of any unauthorized use of your account</li>
                        <li>Ensuring that the lab data you upload belongs to you or that you have
                            authorization to upload it</li>
                    </ul>
                </section>

                <section>
                    <h2>6. Acceptable Use</h2>
                    <p>You agree not to:</p>
                    <ul>
                        <li>Use the Service for any unlawful purpose</li>
                        <li>Upload false, misleading, or fabricated lab data</li>
                        <li>Attempt to reverse-engineer, decompile, or disassemble any part of the Service</li>
                        <li>Interfere with or disrupt the Service or its infrastructure</li>
                        <li>Access another user's account without authorization</li>
                        <li>Use the Service to provide medical advice to others unless you are a licensed
                            healthcare provider using the designated provider features</li>
                        <li>Scrape, harvest, or collect data from the Service by automated means</li>
                    </ul>
                </section>

                <section>
                    <h2>7. Intellectual Property</h2>
                    <p>
                        The Service, including its algorithms, scoring methodology, user interface, design,
                        and content (excluding user-uploaded data), is the property of PrimeMaxing and is
                        protected by intellectual property laws. You are granted a limited, non-exclusive,
                        non-transferable license to use the Service in accordance with these Terms.
                        Licensed healthcare providers may use the Service in connection with their
                        clinical practice through the designated provider features.
                    </p>
                    <p>
                        You retain ownership of the lab data you upload. By uploading data, you grant
                        PrimeMaxing a limited license to process, analyze, and store your data as necessary
                        to provide the Service.
                    </p>
                </section>

                <section>
                    <h2>8. Subscription &amp; Billing</h2>
                    <p>
                        Certain features of the Service may require a paid subscription. By subscribing,
                        you agree to pay the applicable fees. Subscription details, including pricing and
                        billing cycles, will be presented to you before purchase.
                    </p>
                    <p>
                        You may cancel your subscription at any time. Cancellation takes effect at the end
                        of your current billing period. Refunds are provided in accordance with applicable law.
                    </p>
                </section>

                <section>
                    <h2>9. Disclaimer of Warranties</h2>
                    <p>
                        THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND,
                        EITHER EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO IMPLIED WARRANTIES OF
                        MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT.
                    </p>
                    <p>
                        WE DO NOT WARRANT THAT:
                    </p>
                    <ul>
                        <li>The Service will be uninterrupted, timely, secure, or error-free</li>
                        <li>The results obtained from the Service will be accurate or reliable</li>
                        <li>Any health scores, suggestions, or AI outputs are correct, complete, or
                            appropriate for your individual circumstances</li>
                        <li>Defects in the Service will be corrected</li>
                    </ul>
                </section>

                <section>
                    <h2>10. Limitation of Liability</h2>
                    <p>
                        TO THE MAXIMUM EXTENT PERMITTED BY LAW, PRIMEMAXING AND ITS OFFICERS, DIRECTORS,
                        EMPLOYEES, AND AGENTS SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL,
                        CONSEQUENTIAL, OR PUNITIVE DAMAGES, INCLUDING BUT NOT LIMITED TO LOSS OF PROFITS,
                        DATA, USE, GOODWILL, OR OTHER INTANGIBLE LOSSES, ARISING OUT OF OR IN CONNECTION
                        WITH YOUR USE OF THE SERVICE.
                    </p>
                    <p>
                        YOU SPECIFICALLY ACKNOWLEDGE THAT PRIMEMAXING IS NOT LIABLE FOR ANY HEALTH OUTCOMES,
                        MEDICAL DECISIONS, OR CONSEQUENCES ARISING FROM YOUR USE OF OR RELIANCE ON THE
                        SERVICE, ITS SCORES, SUGGESTIONS, OR AI-GENERATED CONTENT.
                    </p>
                </section>

                <section>
                    <h2>11. Indemnification</h2>
                    <p>
                        You agree to indemnify and hold harmless PrimeMaxing, its officers, directors,
                        employees, and agents from any claims, liabilities, damages, losses, and expenses
                        (including reasonable attorneys' fees) arising out of or in connection with your use
                        of the Service, your violation of these Terms, or your violation of any rights of
                        another party.
                    </p>
                </section>

                <section>
                    <h2>12. Dispute Resolution</h2>
                    <p>
                        Any dispute arising out of or relating to these Terms or the Service shall be resolved
                        through binding arbitration in accordance with the rules of the American Arbitration
                        Association. The arbitration shall be conducted on an individual basis — class actions
                        and class arbitrations are not permitted.
                    </p>
                    <p>
                        You agree that any claim must be brought within one (1) year after the cause of
                        action arises, or the claim is permanently barred.
                    </p>
                </section>

                <section>
                    <h2>13. Governing Law</h2>
                    <p>
                        These Terms shall be governed by and construed in accordance with the laws of the
                        State of Texas, without regard to its conflict of law provisions.
                    </p>
                </section>

                <section>
                    <h2>14. Termination</h2>
                    <p>
                        We may suspend or terminate your access to the Service at any time for violation
                        of these Terms or for any other reason, with reasonable notice when practicable.
                        Upon termination, your right to use the Service ceases. Before your account is
                        permanently closed, you will have a reasonable opportunity to export or request
                        a copy of your data, consistent with the rights described in our Privacy Policy.
                        You may delete your account and data at any time through the Service.
                    </p>
                </section>

                <section>
                    <h2>15. Changes to These Terms</h2>
                    <p>
                        We may revise these Terms at any time by posting updated Terms on the Service. Your
                        continued use after changes are posted constitutes acceptance. If we make material
                        changes, we will provide notice through the Service or by email.
                    </p>
                </section>

                <section>
                    <h2>16. Severability</h2>
                    <p>
                        If any provision of these Terms is found to be unenforceable, the remaining provisions
                        will continue in full force and effect.
                    </p>
                </section>

                <section>
                    <h2>17. Contact Us</h2>
                    <p>
                        If you have questions about these Terms, please contact us at:
                    </p>
                    <p>
                        <strong>PrimeMaxing</strong><br />
                        Email: legal@primemaxing.com
                    </p>
                </section>
            </div>
        </div>
    );
}

export default ServiceAgreement;
